import { ReactNode, useEffect } from "react";

export const formatLabel = (value?: string | null) =>
  value
    ? value
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase())
    : "—";

export const formatDate = (value?: string | null, includeTime = true) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  }).format(date);
};

export const dateTimeLocal = (value?: string | null) => {
  const date = value ? new Date(value) : new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export const errorMessage = (error: unknown) => {
  const value = error as any;
  if (typeof value?.message === "string") return value.message;
  if (typeof value?.error === "string") return value.error;
  const errors = value?.errors ?? value?.data?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (typeof first === "string") return first;
  }
  return "The request could not be completed.";
};

export const isPermissionError = (error: unknown) =>
  /permission|forbidden|not authorized/i.test(errorMessage(error));

export function StatusPill({ status }: { status?: string | null }) {
  const tone =
    status === "active" || status === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "scheduled" || status === "in_progress"
        ? "bg-blue-50 text-blue-700 border-blue-200"
        : status === "archived" || status === "cancelled"
          ? "bg-gray-100 text-gray-600 border-gray-200"
          : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone}`}
    >
      {formatLabel(status)}
    </span>
  );
}

export function SectionCard({
  title,
  description,
  action,
  children,
  className = "",
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-gray-200 bg-white ${className}`}
    >
      {(title || action) && (
        <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-left">
            {title && <h2 className="text-left text-base font-semibold text-gray-950">{title}</h2>}
            {description && (
              <p className="mt-1 max-w-2xl text-left text-sm text-gray-500">{description}</p>
            )}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({
  title,
  body,
  icon = "fa-inbox",
}: {
  title: string;
  body: string;
  icon?: string;
}) {
  return (
    <div className="px-6 py-14 text-center">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <i className={`fa ${icon}`} />
      </span>
      <h3 className="mt-4 text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-gray-500">{body}</p>
    </div>
  );
}

export function LoadingState({ rows = 3 }: { rows?: number }) {
  return (
    <div className="animate-pulse divide-y divide-gray-100">
      {Array.from({ length: rows }).map((_, index) => (
        <div className="flex gap-4 px-5 py-5" key={index}>
          <div className="h-9 w-9 rounded-lg bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-gray-100" />
            <div className="h-3 w-2/3 rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const denied = isPermissionError(error);
  return (
    <div className="px-6 py-12 text-center">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-red-50 text-red-600">
        <i className={`fa ${denied ? "fa-lock" : "fa-triangle-exclamation"}`} />
      </span>
      <h3 className="mt-4 text-sm font-semibold text-gray-900">
        {denied ? "Permission required" : "Unable to load this module"}
      </h3>
      <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-gray-500">
        {denied
          ? "Your admin role does not include the permission required for this gamification module."
          : errorMessage(error)}
      </p>
      {onRetry && !denied && (
        <button
          className="mt-4 text-sm font-semibold text-orange-700 hover:text-orange-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
          onClick={onRetry}
          type="button"
        >
          Try again
        </button>
      )}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "danger" | "quiet";
}) {
  const variants = {
    primary: "bg-gray-950 text-white hover:bg-gray-800",
    secondary: "border border-gray-300 bg-white text-gray-800 hover:bg-gray-50",
    danger: "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    quiet: "text-gray-600 hover:bg-gray-100",
  };
  return (
    <button
      {...props}
      className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export const fieldClass =
  "mt-1.5 block w-full rounded-lg border-gray-300 bg-white text-sm text-gray-900 shadow-none focus:border-orange-400 focus:ring-orange-400";

export function Field({
  label,
  hint,
  required,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block text-sm font-medium text-gray-700 ${className}`}>
      {label} {required && <span className="text-orange-600">*</span>}
      {children}
      {hint && <span className="mt-1 block text-xs font-normal text-gray-500">{hint}</span>}
    </label>
  );
}

export function Drawer({
  open,
  title,
  description,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button
        className="absolute inset-0 cursor-default bg-gray-950/50"
        onClick={onClose}
        aria-label="Close panel"
      />
      <div className="relative flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-950">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          <button
            className="ml-4 flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-400"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <i className="fa fa-xmark" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </div>
    </div>
  );
}

export function Stat({
  label,
  value,
  note,
  icon,
}: {
  label: string;
  value: ReactNode;
  note: string;
  icon: string;
}) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-50 text-orange-700">
          <i className={`fa ${icon}`} />
        </span>
      </div>
      <p className="mt-5 text-3xl font-semibold tracking-tight text-gray-950">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{note}</p>
    </div>
  );
}
